/**
 * Billing Estimate History Section
 *
 * Estimate-vs-actual track record: for each completed month, the final
 * unbilled-work estimate from timetoinvoice against the Xero actual.
 * Open (not yet frozen) months are shown as pending. Hidden entirely
 * when no history exists.
 */

import { formatCurrency } from '@/utils/formatters';
import type { BillingEstimateHistoryEntry } from '@/types/revenueTarget';

interface BillingEstimateHistorySectionProps {
  history: BillingEstimateHistoryEntry[];
}

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function monthLabel(key: string): string {
  const [year, month] = key.split('-').map(Number);
  const name = MONTH_NAMES[month - 1] ?? key;
  return `${name} ${year}`;
}

export function BillingEstimateHistorySection({
  history,
}: BillingEstimateHistorySectionProps) {
  if (history.length === 0) return null;

  return (
    <section className="rt-section" aria-label="Estimate vs actual history">
      <h3 className="rt-section__title">Billing estimate vs actual</h3>
      <p className="rt-section__note">
        How the timetoinvoice unbilled estimate compared to the invoiced
        Xero revenue once each month closed.
      </p>
      <div className="rt-table-wrap">
        <table className="rt-table">
          <thead>
            <tr>
              <th scope="col">Month</th>
              <th scope="col">Estimate (ex-GST)</th>
              <th scope="col">Actual revenue</th>
              <th scope="col">Variance</th>
            </tr>
          </thead>
          <tbody>
            {history.map((entry) => (
              <tr key={entry.month}>
                <td>{monthLabel(entry.month)}</td>
                <td>{formatCurrency(entry.estimateSubtotal)}</td>
                <td>
                  {entry.actualRevenue === null
                    ? 'Pending'
                    : formatCurrency(entry.actualRevenue)}
                </td>
                <td
                  className={
                    entry.variance !== null && entry.variance < 0
                      ? 'rt-negative'
                      : ''
                  }
                >
                  {entry.variance === null
                    ? '—'
                    : `${formatCurrency(entry.variance)}${
                        entry.variancePercent === null
                          ? ''
                          : ` (${entry.variancePercent > 0 ? '+' : ''}${entry.variancePercent.toFixed(1)}%)`
                      }`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
